import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'react-toastify';
import moment from 'moment';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { AlertCircle, Download, FileText, Copy, Check, X, ChevronLeft, ChevronRight, Eye, Search } from 'react-feather';
import config from '../../../../config';
import '../../sadmin.css';

const ITEMS_PER_PAGE = 50;

const ErrorLogView = () => {
    const { t } = useTranslation();
    const login_name = localStorage.getItem('loginName');
    const login_id = localStorage.getItem('loginId');

    const token = localStorage.getItem('sessiontoken');
    const configheader = { headers: { Authorization: `${token}` } };

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [reportData, setReportData] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [validationMessage, setValidationMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [modalPayload, setModalPayload] = useState(null);
    const [copied, setCopied] = useState(false);
    const [query, setQuery] = useState('');

    const today = new Date().toISOString().split('T')[0];

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return reportData;
        return reportData.filter((it) =>
            [
                it.concessionaire_code,
                it.tenant_code,
                it.tenant_name,
                it.module,
                it.message,
            ]
                .filter(Boolean)
                .some((v) => String(v).toLowerCase().includes(q)),
        );
    }, [reportData, query]);

    const pageCount = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
    const currentPageData = filtered.slice(currentPage * ITEMS_PER_PAGE, (currentPage + 1) * ITEMS_PER_PAGE);

    useEffect(() => {
        if (currentPage >= pageCount) setCurrentPage(0);
    }, [currentPage, pageCount]);

    const insertToLog = (task) => {
        axios
            .post(
                `${config.hostname}/log`,
                { created_by: login_id, module: 'Super Admin', task: `${login_name}${task}` },
                configheader,
            )
            .catch((err) => console.error(err));
    };

    const generateReport = async (e) => {
        e.preventDefault();
        if (endDate < startDate) {
            setValidationMessage(t('End date cannot be less than start date'));
            return;
        }
        setValidationMessage('');
        setLoading(true);
        try {
            const res = await axios.post(
                `${config.hostname}/log/errorlog`,
                { start_date: startDate, end_date: endDate },
                configheader,
            );
            setReportData(res.data || []);
            setCurrentPage(0);
            insertToLog(' generated error log report');
        } catch (err) {
            console.error(err);
            toast.error(t('Error'));
        } finally {
            setLoading(false);
        }
    };

    const buildExportRows = () =>
        reportData.map((it) => ({
            Date: moment(it.created_date).format('DD/MM/YYYY HH:mm:ss'),
            'Concessionaire Code': it.concessionaire_code || '',
            'Tenant Code': it.tenant_code || '',
            'Tenant Name': it.tenant_name || '',
            Module: it.module || '',
            Error: it.message || '',
        }));

    const handleExportExcel = () => {
        if (reportData.length === 0) return;
        const ws = XLSX.utils.json_to_sheet(buildExportRows());
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Error Log');
        XLSX.writeFile(wb, 'ErrorLog.xlsx');
        insertToLog(' exported error log to excel');
    };

    const handleExportPDF = () => {
        if (reportData.length === 0) return;
        const doc = new jsPDF({ orientation: 'landscape' });
        doc.setFontSize(13);
        doc.text('Error Log', doc.internal.pageSize.getWidth() / 2, 10, { align: 'center' });
        doc.setFontSize(8);
        doc.text(`From: ${startDate}  To: ${endDate}`, 10, 20);
        const rows = buildExportRows();
        const headers = Object.keys(rows[0] || {});
        doc.autoTable({
            startY: 26,
            head: [headers],
            body: rows.map((r) => headers.map((h) => r[h])),
            styles: { fontSize: 7, cellPadding: 2, overflow: 'linebreak' },
        });
        doc.save('ErrorLog.pdf');
        insertToLog(' exported error log to pdf');
    };

    const copyPayload = () => {
        if (!modalPayload) return;
        navigator.clipboard.writeText(modalPayload);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="v3 v3-sadmin">
            <header className="v3-sadmin-hero">
                <div className="v3-sadmin-hero-text">
                    <div className="v3-label">{t('Settings · Diagnostics')}</div>
                    <h1 className="v3-display v3-sadmin-greeting" style={{ fontSize: 26 }}>
                        {t('Error log')}
                    </h1>
                    <p className="v3-body v3-sadmin-sub">
                        {t('Failed integrations and pipeline errors. Click "View" to inspect the raw payload.')}
                    </p>
                </div>
            </header>

            <section className="v3-card v3-sadmin-form-card">
                <form className="v3-sadmin-form" onSubmit={generateReport}>
                    <div className="v3-sadmin-form-grid">
                        <div className="v3-field">
                            <label className="v3-label">{t('Start date')}</label>
                            <input
                                className="v3-input"
                                type="date"
                                value={startDate}
                                max={today}
                                onChange={(e) => {
                                    setStartDate(e.target.value);
                                    setReportData([]);
                                    setCurrentPage(0);
                                }}
                                required
                            />
                        </div>
                        <div className="v3-field">
                            <label className="v3-label">{t('End date')}</label>
                            <input
                                className="v3-input"
                                type="date"
                                value={endDate}
                                max={today}
                                onChange={(e) => {
                                    setEndDate(e.target.value);
                                    setReportData([]);
                                    setCurrentPage(0);
                                }}
                                required
                            />
                            {validationMessage && (
                                <span className="v3-sadmin-field-err">{validationMessage}</span>
                            )}
                        </div>
                    </div>
                    <div className="v3-sadmin-form-actions">
                        <button type="submit" className="v3-btn v3-btn-primary" disabled={loading}>
                            <AlertCircle size={14} />
                            {loading ? t('Generating…') : t('Generate report')}
                        </button>
                    </div>
                </form>
            </section>

            <section className="v3-card v3-sadmin-list-card">
                <div className="v3-sadmin-list-header">
                    <div className="v3-sadmin-list-title">
                        <h2 className="v3-heading">{t('Errors')}</h2>
                        <span className="v3-sadmin-list-count">{filtered.length}</span>
                    </div>
                    <div className="v3-sadmin-list-actions">
                        <div className="v3-sadmin-search">
                            <Search size={14} aria-hidden />
                            <input
                                type="search"
                                placeholder={t('Search code, tenant, module, error')}
                                value={query}
                                onChange={(e) => {
                                    setQuery(e.target.value);
                                    setCurrentPage(0);
                                }}
                                disabled={reportData.length === 0}
                                aria-label={t('Search results')}
                            />
                        </div>
                        <button
                            type="button"
                            className="v3-btn v3-btn-ghost"
                            onClick={handleExportExcel}
                            disabled={reportData.length === 0}
                        >
                            <Download size={14} /> {t('Excel')}
                        </button>
                        <button
                            type="button"
                            className="v3-btn v3-btn-ghost"
                            onClick={handleExportPDF}
                            disabled={reportData.length === 0}
                        >
                            <FileText size={14} /> {t('PDF')}
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="v3-sadmin-empty">
                        <p>{t('Loading…')}</p>
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="v3-sadmin-empty">
                        <AlertCircle size={28} aria-hidden />
                        <h3>{reportData.length === 0 ? t('No errors') : t('No matches')}</h3>
                        <p>
                            {reportData.length === 0
                                ? t('Generate a report for the chosen window.')
                                : t('Try a different search term.')}
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="v3-sadmin-table-wrap">
                            <table className="v3-sadmin-table">
                                <thead>
                                    <tr>
                                        <th>{t('Date')}</th>
                                        <th>{t('Concessionaire')}</th>
                                        <th>{t('Tenant code')}</th>
                                        <th>{t('Tenant name')}</th>
                                        <th>{t('Module')}</th>
                                        <th>{t('Error')}</th>
                                        <th style={{ textAlign: 'right' }}>{t('Payload')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentPageData.map((it, i) => (
                                        <tr key={i} style={{ cursor: 'default' }}>
                                            <td className="v3-sadmin-cell-muted">
                                                {moment(it.created_date).format('DD/MM/YYYY HH:mm:ss')}
                                            </td>
                                            <td>
                                                <span className="v3-sadmin-cell-code">{it.concessionaire_code}</span>
                                            </td>
                                            <td>
                                                <span className="v3-sadmin-cell-code">{it.tenant_code}</span>
                                            </td>
                                            <td className="v3-sadmin-cell-strong">{it.tenant_name}</td>
                                            <td>{it.module}</td>
                                            <td style={{ color: 'var(--v3-red)' }}>{it.message}</td>
                                            <td style={{ textAlign: 'right' }}>
                                                <button
                                                    type="button"
                                                    className="v3-sadmin-icon-btn"
                                                    title={t('View payload')}
                                                    onClick={() => {
                                                        setModalPayload(it.payload);
                                                        setCopied(false);
                                                    }}
                                                >
                                                    <Eye size={13} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {filtered.length > ITEMS_PER_PAGE && (
                            <div className="v3-sadmin-pagination">
                                <span>
                                    {t('Page')} <strong>{currentPage + 1}</strong> {t('of')} <strong>{pageCount}</strong>
                                </span>
                                <div className="v3-sadmin-pagination-controls">
                                    <button
                                        type="button"
                                        className="v3-sadmin-page-btn"
                                        onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                                        disabled={currentPage === 0}
                                    >
                                        <ChevronLeft size={13} />
                                    </button>
                                    <button
                                        type="button"
                                        className="v3-sadmin-page-btn"
                                        onClick={() => setCurrentPage((p) => Math.min(pageCount - 1, p + 1))}
                                        disabled={currentPage >= pageCount - 1}
                                    >
                                        <ChevronRight size={13} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </section>

            {modalPayload !== null && (
                <div
                    className="v3-sadmin-modal-backdrop"
                    role="dialog"
                    aria-modal="true"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setModalPayload(null);
                    }}
                >
                    <div className="v3-card v3-sadmin-modal">
                        <div className="v3-sadmin-modal-head">
                            <h2 className="v3-heading">{t('Payload')}</h2>
                            <button
                                type="button"
                                className="v3-sadmin-modal-x"
                                onClick={() => setModalPayload(null)}
                                aria-label={t('Close')}
                            >
                                <X size={14} />
                            </button>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                                type="button"
                                className={`v3-btn ${copied ? 'v3-btn-ghost' : 'v3-btn-ghost'}`}
                                onClick={copyPayload}
                                style={copied ? { color: 'var(--v3-green)', borderColor: 'var(--v3-green)' } : {}}
                            >
                                {copied ? <Check size={14} /> : <Copy size={14} />}
                                {copied ? t('Copied') : t('Copy')}
                            </button>
                        </div>
                        <pre className="v3-sadmin-json">{String(modalPayload || '')}</pre>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ErrorLogView;
